import { Directive, ElementRef, OnInit, HostBinding, HostListener, Input } from '@angular/core';
import { KeyboardNavigationHelper } from './keyboard-navigation.helper';

@Directive({
  selector: '[appDatatableCell]'
})
export class DatatableCellDirective implements OnInit {

  @Input() rowIndex: number = 0;
  @Input() colIndex: number = 0;
  @Input() columnName: string = '';
  @Input() cellValue: any;
  @Input() tableId: string = '';
  @Input() isLink: boolean = false;
  @Input() isExternalLink: boolean = false;
  @Input() isEmailLink: boolean = false;
  @Input() isFileLink: boolean = false;
  @Input() isEncrypted: boolean = false;

  // Roving tabindex: dynamically set based on whether this cell is currently focused
  // First and last column cells are always focusable for Tab navigation to/from actions column
  // Other cells are only focusable when they have focus (roving tabindex pattern)
  @HostBinding('attr.tabindex')
  get tabindex(): string {
    // First column: Tab from actions goes to first cell of same row
    // Last column: Shift+Tab from actions goes to last cell of previous row
    // All other cells use roving tabindex pattern
    const isFirstCol = this.colIndex === 0;
    const isLastCol = this.isLastColumn();

    return isFirstCol || isLastCol || this.isFocused ? '0' : '-1';
  }

  // ARIA label that screen readers will announce
  // WCAG AA compliant: Column name, content, row position, type, and interaction instructions
  @HostBinding('attr.aria-label')
  get ariaLabel() {
    const parts: string[] = [];

    // 1. Column name (provides context for what the data represents)
    parts.push(this.columnName);

    // 2. Cell content (actual value)
    const cellContent = this.getCellText();
    if (cellContent && cellContent !== 'Empty') {
      parts.push(cellContent);
    }

    // 3. Row number (navigation metadata)
    parts.push(`Row ${this.rowIndex + 1}`);

    // 4. Cell type information
    if (this.isFileLink) {
      parts.push('file link');
    } else if (this.isExternalLink) {
      parts.push('external link');
    } else if (this.isLink) {
      parts.push('link');
    } else if (this.isEmailLink) {
      parts.push('email link');
    } else if (this.isEncrypted) {
      parts.push('encrypted field');
    }

    // 5. Interaction instructions
    if (this.isLink || this.isExternalLink || this.isFileLink || this.isEmailLink) {
      parts.push('press Space or Enter to activate');
    }

    return parts.join(', ');
  }

  // Track if this cell is currently focused
  private isFocused = false;

  constructor(private readonly el: ElementRef) {}

  ngOnInit() {
    // Outline is handled by CSS
    this.el.nativeElement.style.outline = 'none';
  }

  // Get text content from cell for screen reader
  private getCellText(): string {
    if (this.cellValue !== null && this.cellValue !== undefined) {
      return String(this.cellValue);
    }
    // Fallback to element text content
    const text = this.el.nativeElement.textContent?.trim();
    return text || 'Empty';
  }

  // Handle keyboard navigation
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    let targetRow: number | string = this.rowIndex;
    let targetCol = this.colIndex;
    let shouldNavigate = false;

    switch (event.key) {
      case ' ':
      case 'Enter': {
        this.handleActivateCell(event);
        break;
      }
      case 'ArrowUp': {
        const result = this.handleVerticalArrow('up');
        if (result !== null) {
          targetRow = result;
          shouldNavigate = true;
        }
        break;
      }
      case 'ArrowDown': {
        const result = this.handleVerticalArrow('down');
        if (result !== null) {
          targetRow = result;
          shouldNavigate = true;
        }
        break;
      }
      case 'ArrowLeft':
        if (this.handleArrowLeft(event)) {
          return; // Handled navigation to action buttons
        }
        targetCol = this.colIndex - 1;
        shouldNavigate = true;
        break;
      case 'ArrowRight':
        targetCol = this.colIndex + 1;
        shouldNavigate = true;
        break;
      case 'Tab': {
        const result = this.handleTabNavigation(event);
        if (result !== null) {
          targetCol = result;
          shouldNavigate = true;
        }
        break;
      }
    }

    if (shouldNavigate) {
      event.preventDefault();
      event.stopPropagation();
      this.navigateToCell(targetRow, targetCol);
    }
  }

  private handleActivateCell(event: KeyboardEvent): void {
    // Try to find and activate a clickable link or button within the cell
    const clickableElement = this.el.nativeElement.querySelector('a, button:not([disabled])');
    if (clickableElement) {
      event.preventDefault();
      event.stopPropagation();
      (clickableElement as HTMLElement).click();
    }
    // If no clickable element, do nothing (let default behavior happen)
  }

  private handleVerticalArrow(direction: 'up' | 'down'): number | string | null {
    const targetRow = this.findNextVisibleRow(direction);
    return targetRow !== null ? targetRow : null;
  }

  private handleArrowLeft(event: KeyboardEvent): boolean {
    if (this.colIndex === 0) {
      // First cell - go to last action button of same row
      this.navigateToLastActionButton(this.rowIndex);
      event.preventDefault();
      event.stopPropagation();
      return true; // Indicate we handled navigation
    }
    return false; // Let caller handle normal column navigation
  }

  private handleTabNavigation(event: KeyboardEvent): number | null {
    const isFirstColumn = this.colIndex === 0;
    const isLastColumn = this.isLastColumn();

    if (event.shiftKey) {
      // Shift+Tab backward
      if (!isFirstColumn) {
        return this.colIndex - 1;
      }
      // At first column, let Tab naturally go to previous element
      return null;
    } else if (!isLastColumn) {
      // Tab forward - normal forward navigation within row
      return this.colIndex + 1;
    }
    // At last column, let Tab naturally go to next element
    return null;
  }

  // Navigate to the specified cell
  private navigateToCell(targetRow: number | string, targetCol: number) {
    // Find the target cell in the DOM
    const targetCell = this.findCellByPosition(targetRow, targetCol);

    if (targetCell) {
      // Update tabindex for roving pattern
      this.updateTabindex(targetCell);

      // Focus the target cell
      targetCell.focus();

      // Scroll the cell into view if it's outside the viewport
      targetCell.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });

    }
  }

  // Find cell element by row and column index using data attributes
  private findCellByPosition(rowIndex: number | string, colIndex: number): HTMLElement | null {
    const selector = `[data-table-id="${this.tableId}"] [data-row-index="${rowIndex}"][data-col-index="${colIndex}"]`;
    return document.querySelector(selector) as HTMLElement;
  }

  // Check if this is the last column (no cell to the right)
  private isLastColumn(): boolean {
    const nextCell = this.findCellByPosition(this.rowIndex, this.colIndex + 1);
    return nextCell === null;
  }

  // Find the next visible row index in the specified direction
  // Returns null if at boundary or no rows found
  private findNextVisibleRow(direction: 'up' | 'down'): number | string | null {
    return KeyboardNavigationHelper.findNextVisibleRow(this.rowIndex, this.tableId, direction);
  }

  // Navigate to last action button in the same row
  private navigateToLastActionButton(rowIndex: number | string) {
    // Query DOM for all action buttons in this row
    const selector = `[data-table-id="${this.tableId}"] [data-action-row="${rowIndex}"]`;
    const buttons = Array.from(document.querySelectorAll(selector)) as HTMLElement[];

    if (buttons.length > 0) {
      // Focus the last button
      buttons[buttons.length - 1].focus();
    }
  }

  // Update tabindex for roving pattern
  private updateTabindex(targetCell: HTMLElement) {
    // Find the currently focused cell (tabindex="0") and set it to "-1"
    const currentFocusedCell = document.querySelector(`[data-table-id="${this.tableId}"] [appDatatableCell][tabindex="0"]`);
    if (currentFocusedCell && currentFocusedCell !== targetCell) {
      currentFocusedCell.setAttribute('tabindex', '-1');
    }

    // Set target cell to tabindex="0"
    targetCell.setAttribute('tabindex', '0');
  }

  // Show keyboard focus with blurple outline
  @HostListener('focus')
  onFocus() {
    this.isFocused = true;
  }

  // Clear focus state
  @HostListener('blur')
  onBlur() {
    this.isFocused = false;
  }
}
